/**
 * STORE DE AUTENTICACIÓN - PINIA
 *
 * Este store maneja todo el estado relacionado con autenticación:
 * - Usuario actual
 * - Tokens JWT
 * - Estado de sesión
 * - Permisos
 *
 * PINIA vs VUEX:
 * Pinia es el store oficial de Vue 3, más simple y con mejor TypeScript support.
 * Usa Composition API en lugar de mutations/actions.
 */

import  { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { loginWithPin, logout as apiLogout, getAccessToken, getRefreshToken, clearTokens } from '@/utils/api.js'
import { USER_ROLES, hasPermission, isAdmin, canUsePOS } from '@/utils/constants.js'

/**
 * DEFINICIÓN DEL STORE
 *
 * defineStore() crea un store de Pinia
 * Primer parámetro: ID único del store
 * Segundo parámetro: función setup que define el estado y acciones
 */
export const useAuthStore = defineStore('auth', () => {
  /**
   * ESTADO REACTIVO
   *
   * ref() crea estado reactivo mutable
   * computed() crea propiedades calculadas
   */

  // Usuario actual (objeto completo del backend)
  const user = ref(null)

  // MULTI-TENANT: Organización del usuario
  const organization = ref(null)

  // Tokens JWT
  const accessToken = ref(getAccessToken())
  const refreshToken = ref(getRefreshToken())

  // Estado de carga durante operaciones de auth
  const isLoading = ref(false)

  // Errores de autenticación
  const authError = ref(null)

  /**
   * COMPUTED PROPERTIES
   *
   * Propiedades calculadas que se actualizan automáticamente
   * cuando cambian las dependencias
   */

  // Si el usuario está autenticado
  const isAuthenticated = computed(() => {
    return !!(accessToken.value && user.value)
  })

  // Rol del usuario actual
  const userRole = computed(() => {
    return user.value?.role || null
  })

  // Si el usuario es administrador
  const isUserAdmin = computed(() => {
    return isAdmin(userRole.value)
  })

  // Si el usuario puede usar el POS
  const canUserUsePOS = computed(() => {
    return canUsePOS(userRole.value)
  })

  // Nombre para mostrar del usuario
  const displayName = computed(() => {
    if (!user.value) return ''

    // Prioridad: first_name + last_name, luego username
    if (user.value.first_name || user.value.last_name) {
      return `${user.value.first_name || ''} ${user.value.last_name || ''}`.trim()
    }

    return user.value.username || ''
  })

  // Sesión de trabajo actual
  const currentWorkSession = computed(() => {
    return user.value?.current_session || null
  })

  // Si tiene sesión de trabajo activa
  const hasActiveWorkSession = computed(() => {
    return !!currentWorkSession.value
  })

  /**
   * MULTI-TENANT: COMPUTED PROPERTIES DE ORGANIZACIÓN
   */

  // Organización actual
  const currentOrganization = computed(() => organization.value)

  // Nombre de la organización
  const organizationName = computed(() => organization.value?.name || '')

  // Slug de la organización
  const organizationSlug = computed(() => organization.value?.slug || '')

  // Si la organización está en trial
  const isTrialActive = computed(() => organization.value?.is_trial || false)

  // Fecha de fin del trial
  const trialEndsAt = computed(() => organization.value?.trial_ends_at)

  // Si la organización está activa
  const isOrganizationActive = computed(() => organization.value?.is_active ?? true)

  // Días restantes de trial
  const trialDaysRemaining = computed(() => {
    if (!organization.value?.is_trial || !organization.value?.trial_ends_at) {
      return null
    }
    const now = new Date()
    const trialEnd = new Date(organization.value.trial_ends_at)
    const diffTime = trialEnd - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  })

  // Si el trial ha expirado
  const isTrialExpired = computed(() => {
    if (!organization.value?.is_trial || !organization.value?.trial_ends_at) {
      return false
    }
    const now = new Date()
    const trialEnd = new Date(organization.value.trial_ends_at)
    return now > trialEnd
  })

  /**
   * ACCIONES (METHODS)
   *
   * Funciones que modifican el estado o ejecutan lógica
   */

  /**
   * LOGIN CON PIN
   *
   * Autentica usuario usando PIN de 4 dígitos
   */
  const login = async (pin, startSession = false, startingCash = 0) => {
    // Limpiar errores previos
    authError.value = null
    isLoading.value = true

    try {
      // Validar PIN básico
      if (!pin || pin.length !== 4) {
        throw new Error('El PIN debe tener 4 dígitos')
      }

      console.log('🔐 Intentando login con PIN...')

      // Hacer request al backend
      const response = await loginWithPin(pin, startSession, startingCash)

      // Extraer datos de la respuesta
      const { access, refresh, user: userData } = response

      // MULTI-TENANT: Extraer organización del usuario
      const organizationData = userData.organization

      // Actualizar estado
      accessToken.value = access
      refreshToken.value = refresh
      user.value = userData
      organization.value = organizationData  // ← NUEVO

      console.log('✅ Login exitoso:', userData.username, '-', organizationData?.name)

      return {
        success: true,
        user: userData,
        organization: organizationData,  // ← NUEVO
        hasSession: !!userData.current_session
      }

    } catch (error) {
      console.error('❌ Error en login:', error)

      // Limpiar estado en caso de error
      accessToken.value = null
      refreshToken.value = null
      user.value = null
      organization.value = null  // ← NUEVO

      // Mapear errores específicos
      if (error.response?.status === 400) {
        authError.value = 'PIN incorrecto'
      } else if (error.response?.status === 429) {
        authError.value = 'Demasiados intentos. Espera un momento.'
      } else if (error.message.includes('PIN')) {
        authError.value = error.message
      } else {
        authError.value = 'Error de conexión. Intenta nuevamente.'
      }

      return {
        success: false,
        error: authError.value
      }

    } finally {
      isLoading.value = false
    }
  }

  /**
   * LOGOUT
   *
   * Cierra sesión del usuario y limpia el estado
   */
  const logout = async (closeSession = false, endingCash = 0, notes = '') => {
    isLoading.value = true

    try {
      console.log('🚪 Cerrando sesión...')

      // Hacer request de logout al backend
      await apiLogout(closeSession, endingCash, notes)

      console.log('✅ Logout exitoso')

    } catch (error) {
      console.error('❌ Error en logout:', error)
      // Continuar con logout local aunque falle el servidor
    } finally {
      // Siempre limpiar estado local
      accessToken.value = null
      refreshToken.value = null
      user.value = null
      organization.value = null  // ← NUEVO
      authError.value = null
      isLoading.value = false

      // Resetear store de sesiones de trabajo
      const { useWorkSessionStore } = await import('./workSession.js')
      const sessionStore = useWorkSessionStore()
      sessionStore.resetState()

      // Resetear store de productos
      const { useProductsStore } = await import('./products.js')
      const productsStore = useProductsStore()
      productsStore.resetState()

      console.log('🧹 Estado de auth limpiado')
    }
  }

  /**
   * RESTAURAR SESIÓN
   *
   * Intenta restaurar la sesión desde localStorage al inicializar la app
   */
  const restoreSession = () => {
    try {
      console.log('🔄 Restaurando sesión...')

      // Verificar tokens
      const storedAccessToken = getAccessToken()
      const storedRefreshToken = getRefreshToken()

      if (!storedAccessToken || !storedRefreshToken) {
        console.log('⚠️ No hay tokens almacenados')
        return false
      }

      // Verificar datos de usuario
      const storedUserData = localStorage.getItem('user_data')
      if (!storedUserData) {
        console.log('⚠️ No hay datos de usuario almacenados')
        clearTokens() // Limpiar tokens huérfanos
        return false
      }

      // MULTI-TENANT: Verificar datos de organización
      const storedOrganizationData = localStorage.getItem('organization_data')
      if (!storedOrganizationData) {
        console.log('⚠️ No hay datos de organización almacenados')
        clearTokens() // Limpiar tokens huérfanos
        return false
      }

      // Parsear datos de usuario y organización
      const userData = JSON.parse(storedUserData)
      const organizationData = JSON.parse(storedOrganizationData)

      // Actualizar estado
      accessToken.value = storedAccessToken
      refreshToken.value = storedRefreshToken
      user.value = userData
      organization.value = organizationData  // ← NUEVO

      console.log('✅ Sesión restaurada:', userData.username, '-', organizationData.name)
      return true

    } catch (error) {
      console.error('❌ Error al restaurar sesión:', error)
      clearTokens()
      return false
    }
  }

  /**
   * ACTUALIZAR USUARIO
   *
   * Actualiza los datos del usuario (útil después de cambios en el perfil)
   */
  const updateUser = (userData) => {
    if (userData) {
      user.value = { ...user.value, ...userData }

      // Actualizar localStorage
      localStorage.setItem('user_data', JSON.stringify(user.value))

      console.log('📝 Datos de usuario actualizados')
    }
  }

  /**
   * VERIFICAR PERMISOS
   *
   * Verifica si el usuario actual tiene un permiso específico
   */
  const checkPermission = (permission) => {
    return hasPermission(userRole.value, permission)
  }

  /**
   * LIMPIAR ERRORES
   *
   * Limpia mensajes de error (útil para UX)
   */
  const clearError = () => {
    authError.value = null
  }

  /**
   * WATCHERS
   *
   * Observadores que reaccionan a cambios en el estado
   */

  // Observar cambios en user para actualizar localStorage
  watch(
    user,
    (newUser) => {
      if (newUser) {
        localStorage.setItem('user_data', JSON.stringify(newUser))
      } else {
        localStorage.removeItem('user_data')
      }
    },
    { deep: true } // Observar cambios profundos en el objeto
  )

  // MULTI-TENANT: Observar cambios en organization para actualizar localStorage
  watch(
    organization,
    (newOrganization) => {
      if (newOrganization) {
        localStorage.setItem('organization_data', JSON.stringify(newOrganization))
      } else {
        localStorage.removeItem('organization_data')
      }
    },
    { deep: true }
  )

  // Observar tokens para sincronizar con localStorage
  watch(
    [accessToken, refreshToken],
    ([newAccessToken, newRefreshToken]) => {
      if (!newAccessToken || !newRefreshToken) {
        clearTokens()
      }
    }
  )

  /**
   * INICIALIZACIÓN
   *
   * Código que se ejecuta al crear el store
   */

  // Intentar restaurar sesión al inicializar
  restoreSession()

  // Escuchar evento de token expirado desde api.js
  if (typeof window !== 'undefined') {
    window.addEventListener('auth:token-expired', () => {
      console.log('🔔 Token expirado detectado - limpiando estado')
      logout()
    })

    // MULTI-TENANT: Escuchar eventos de organización
    window.addEventListener('organization:deactivated', (event) => {
      console.error('🚫 Organización desactivada - cerrando sesión')
      authError.value = event.detail.message || 'Tu organización ha sido desactivada'
      logout()
    })

    window.addEventListener('organization:trial-expired', (event) => {
      console.warn('⚠️ Trial expirado - modo lectura')
      authError.value = event.detail.message || 'El período de prueba ha expirado'
    })
  }

  /**
   * API PÚBLICA DEL STORE
   *
   * Todo lo que retornamos aquí estará disponible
   * cuando se use useAuthStore() en componentes
   */
  return {
    // Estado
    user,
    organization,  // ← NUEVO
    accessToken,
    refreshToken,
    isLoading,
    authError,

    // Computed
    isAuthenticated,
    userRole,
    isUserAdmin,
    canUserUsePOS,
    displayName,
    currentWorkSession,
    hasActiveWorkSession,

    // MULTI-TENANT: Computed de organización
    currentOrganization,
    organizationName,
    organizationSlug,
    isTrialActive,
    trialEndsAt,
    isOrganizationActive,
    trialDaysRemaining,
    isTrialExpired,

    // Acciones
    login,
    logout,
    restoreSession,
    updateUser,
    checkPermission,
    clearError
  }
})

/**
 * UTILIDADES ADICIONALES
 *
 * Funciones helper que pueden ser útiles fuera del store
 */

// Verificar si el store está inicializado y autenticado
export const isStoreAuthenticated = () => {
  try {
    const authStore = useAuthStore()
    return authStore.isAuthenticated
  } catch {
    return false
  }
}

// Obtener rol del usuario actual
export const getCurrentUserRole = () => {
  try {
    const authStore = useAuthStore()
    return authStore.userRole
  } catch {
    return null
  }
}

// Hook para usar en router guards
export const requireAuth = () => {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    throw new Error('Autenticación requerida')
  }

  return authStore
}

// Hook para requerir permisos específicos
export const requirePermission = (permission) => {
  const authStore = requireAuth()

  if (!authStore.checkPermission(permission)) {
    throw new Error(`Permiso requerido: ${permission}`)
  }

  return authStore
}